import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export default function ForgotPassword() {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [touched, setTouched] = useState(false);

  // Email validation
  const validateEmail = (value: string): string | undefined => {
    if (!value) return '이메일을 입력해주세요';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return '올바른 이메일 형식이 아닙니다 (예: user@example.com)';
    return undefined;
  };

  const emailError = useMemo(() => {
    return touched ? validateEmail(email) : undefined;
  }, [email, touched]);

  const isFormValid = useMemo(() => {
    return email && !validateEmail(email);
  }, [email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);

    const error = validateEmail(email);
    if (error) return;

    setIsLoading(true);
    setStatus('idle');

    try {
      await resetPassword(email);
      setStatus('success');
    } catch (err) {
      const message = err instanceof Error ? err.message : '요청에 실패했습니다';
      // Provide helpful error messages
      if (message.includes('not found') || message.includes('exist')) {
        setErrorMessage('등록되지 않은 이메일입니다. 이메일 주소를 확인하거나 회원가입해주세요.');
      } else if (message.includes('rate') || message.includes('too many')) {
        setErrorMessage('요청이 너무 많습니다. 잠시 후 다시 시도해주세요.');
      } else {
        setErrorMessage(message);
      }
      setStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  if (status === 'success') {
    return (
      <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="card">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">이메일을 확인해주세요</h1>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              <span className="font-medium text-gray-900 dark:text-white">{email}</span>로
              <br />비밀번호 재설정 링크를 보냈습니다.
            </p>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>📧 이메일이 안 보이나요?</strong>
              </p>
              <ul className="mt-2 text-sm text-blue-600 dark:text-blue-400 list-disc list-inside space-y-1">
                <li>스팸 또는 정크 메일함을 확인해주세요</li>
                <li>이메일 도착까지 몇 분 걸릴 수 있습니다</li>
              </ul>
            </div>

            <Link 
              to="/login" 
              className="text-space-blue hover:underline text-sm focus:outline-none focus:ring-2 focus:ring-space-blue rounded"
            >
              로그인 페이지로 돌아가기
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">비밀번호 찾기</h1>
          <p className="text-gray-700 dark:text-gray-300">가입한 이메일 주소를 입력하시면<br />비밀번호 재설정 링크를 보내드립니다.</p>
        </div>

        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {status === 'error' && (
              <div 
                className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm"
                role="alert"
                aria-live="polite"
              >
                {errorMessage}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                이메일
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched(true)}
                className={`input ${touched && emailError ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="email@example.com"
                autoComplete="email"
                aria-describedby={emailError ? 'email-error' : undefined}
                aria-invalid={touched && !!emailError}
                required
              />
              {touched && emailError && (
                <p id="email-error" className="mt-1 text-sm text-red-500" role="alert">
                  {emailError}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading || !isFormValid}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-space-blue focus:ring-offset-2"
              aria-busy={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  전송 중...
                </span>
              ) : '재설정 링크 보내기'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-700 dark:text-gray-300">
            비밀번호가 기억나셨나요?{' '}
            <Link 
              to="/login" 
              className="text-space-blue font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-space-blue focus:ring-offset-2 rounded"
            >
              로그인
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
